import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import type { QueryClient } from "@tanstack/react-query"
import { useCallback } from "react"
import type { CompanyApi } from "../../../../hooks/useCompanyApi"
import { deleteMeetingPermanently, hideMeeting, unhideMeeting, updateSubject } from "../../../../lib/api"
import { notifyError } from "../../../../lib/notify"
import { queryKeys } from "../../../../lib/query-keys"

export interface IMeetingLifecycleArgs {
  company: CompanyApi | null
  meetingId: string
  orgId: string | null
  queryClient: QueryClient
  refetch: () => unknown
  navigateHome: () => void
  isOrgAdmin: boolean
  isOrganizer: boolean
  hidden: boolean
}

export interface IMeetingLifecycleActions {
  onRenameSubject: (subject: string) => Promise<void>
  onToggleHidden: (() => void) | undefined
  onDelete: (() => void) | undefined
}

/**
 * Rename / hide / delete for a finished meeting. Shared by the Lexicanon
 * summary surface and the lite finished-meeting page so the confirm copy and
 * cache invalidation stay in lockstep.
 */
export function useMeetingLifecycleActions(args: IMeetingLifecycleArgs): IMeetingLifecycleActions {
  const { company, meetingId, orgId, queryClient, refetch, navigateHome, isOrgAdmin, isOrganizer, hidden } = args
  const { i18n } = useLingui()
  const id = meetingId
  const canHideOrDelete = isOrgAdmin || isOrganizer

  const onRenameSubject = useCallback(
    async (subject: string): Promise<void> => {
      try {
        if (!company) throw new Error("company unavailable")
        await updateSubject(company, id, subject)
        await refetch()
        notifications.show({ message: i18n._(msg`Meeting renamed`), color: "success" })
      } catch (err) {
        notifyError(err, i18n._(msg`Rename failed`))
        throw err
      }
    },
    [company, i18n, id, refetch],
  )

  const onToggleHidden = useCallback((): void => {
    if (!company) return
    const willHide = !hidden
    const run = (): void => {
      ;(willHide ? hideMeeting(company, id) : unhideMeeting(company, id))
        .then(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.meeting(orgId, id) })
          queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
          notifications.show({
            message: willHide ? i18n._(msg`Meeting hidden`) : i18n._(msg`Meeting unhidden`),
            color: willHide ? "gray" : "success",
          })
        })
        .catch((err) => {
          notifyError(err, i18n._(msg`Action failed`))
        })
    }
    if (!willHide) {
      run()
      return
    }
    modals.openConfirmModal({
      title: i18n._(msg`Hide this meeting?`),
      children: (
        <Text size="sm">
          <Trans>Hidden meetings are removed from everyone's list until an admin restores them.</Trans>
        </Text>
      ),
      labels: { confirm: i18n._(msg`Hide`), cancel: i18n._(msg`Cancel`) },
      onConfirm: run,
    })
  }, [company, hidden, i18n, id, orgId, queryClient])

  const onDelete = useCallback((): void => {
    if (!company) return
    modals.openConfirmModal({
      title: isOrgAdmin ? i18n._(msg`Delete meeting permanently?`) : i18n._(msg`Delete this meeting?`),
      children: (
        <Text size="sm">
          {isOrgAdmin ? (
            <Trans>This action cannot be undone. The recording, transcript, and analysis are removed.</Trans>
          ) : (
            <Trans>It is removed from everyone's list. An org admin can restore it or delete it for good.</Trans>
          )}
        </Text>
      ),
      labels: { confirm: i18n._(msg`Delete`), cancel: i18n._(msg`Cancel`) },
      confirmProps: { color: "danger" },
      onConfirm: () => {
        const run = isOrgAdmin ? deleteMeetingPermanently(company, id) : hideMeeting(company, id)
        run
          .then(() => {
            notifications.show({
              message: isOrgAdmin ? i18n._(msg`Meeting permanently deleted`) : i18n._(msg`Meeting deleted`),
              color: "danger",
            })
            queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
            navigateHome()
          })
          .catch((err) => {
            notifyError(err, i18n._(msg`Delete failed`))
          })
      },
    })
  }, [company, i18n, id, isOrgAdmin, navigateHome, orgId, queryClient])

  return {
    onRenameSubject,
    onToggleHidden: canHideOrDelete ? onToggleHidden : undefined,
    onDelete: canHideOrDelete ? onDelete : undefined,
  }
}
